// Command Handler - processes chat commands for Multi-Entity Terrarium
import { useTerrariumStore, PLANT_TYPES, INSECT_TYPES, FLYING_INSECTS, ALL_PLANT_TYPES, ALL_INSECT_TYPES, LEGENDARY_PLANT_TYPES, LEGENDARY_INSECT_TYPES } from '../store/terrariumStore'
import useSettingsStore from '../store/settingsStore'
import useBitsStore, { COSMETIC_PRODUCTS, EFFECT_PRODUCTS, PERK_THRESHOLDS } from '../store/bitsStore'
import { twitchApi } from './twitchApi'

const getSettings = () => useSettingsStore.getState().settings

const sendChat = async (message) => {
  console.log(`[Bot] ${message}`)
  await twitchApi.sendMessage(message)
}

class CommandHandler {
  constructor() {
    this.commands = new Map()
    this.registerCommands()
  }

  registerCommands() {
    // ============================================
    // JOIN / LEAVE (Tier-gated)
    // ============================================

    // !join plant [type] / !join insect [type]
    this.commands.set('join', {
      requiresTier: true,
      handler: async (userId, username, args, store) => {
        const settings = getSettings()
        const bitsStore = useBitsStore.getState()

        let entityKind = args[0]?.toLowerCase()
        let entityType = args[1]?.toLowerCase()

        // Smart detection: if first arg is a known insect or plant type (not "insect"/"plant"/"bug"),
        // treat it as a direct type join — e.g. !join butterfly, !join rose
        if (entityKind && entityKind !== 'insect' && entityKind !== 'bug' && entityKind !== 'plant') {
          if (ALL_INSECT_TYPES.includes(entityKind)) {
            entityType = entityKind
            entityKind = 'insect'
          } else if (ALL_PLANT_TYPES.includes(entityKind)) {
            entityType = entityKind
            entityKind = 'plant'
          }
        }

        // Determine if joining as insect
        const isInsectJoin = entityKind === 'insect' || entityKind === 'bug'

        // Check if user already has an entity
        const existingEntity = store.getEntity(userId)
        if (existingEntity) {
          // Check for second slot perks
          if (existingEntity.type === 'plant' && isInsectJoin) {
            if (settings.enableBitsFeatures && bitsStore.hasReachedPerk(userId, 'second_insect')) {
              // Allow — they can have both a plant and an insect
            } else {
              await sendChat(`@${username} You already have a plant! Use !leave first, or use 1,000 total bits for a second insect slot.`)
              return
            }
          } else if (existingEntity.type === 'insect' && !isInsectJoin) {
            if (settings.enableBitsFeatures && bitsStore.hasReachedPerk(userId, 'second_plant')) {
              // Allow
            } else {
              await sendChat(`@${username} You already have an insect! Use !leave first, or use 500 total bits for a second plant slot.`)
              return
            }
          } else {
            await sendChat(`@${username} You already have an entity of that type! Use !leave first.`)
            return
          }
        }

        if (isInsectJoin) {
          // Check if legendary insect
          let type
          if (LEGENDARY_INSECT_TYPES.includes(entityType)) {
            const cosmeticId = `insect_${entityType}`
            if (!settings.enableBitsFeatures || !bitsStore.hasCosmetic(userId, cosmeticId)) {
              await sendChat(`@${username} ${entityType} is a legendary insect! Use 500 bits to unlock it.`)
              return
            }
            type = entityType
          } else {
            type = ALL_INSECT_TYPES.includes(entityType) ? entityType : settings.defaultInsectType || 'ladybug'
          }

          const result = await store.addInsect(userId, username, type)
          if (result.success) {
            const joinMsg = FLYING_INSECTS.includes(type) ? "Buzz around with !fly or !land" : "Crawl around with !visit or !dance"
            await sendChat(`🐛 @${username} joined as a ${type}! ${joinMsg}`)
          } else if (result.error === 'max_insects') {
            await sendChat(`@${username} The terrarium is full of insects!`)
          } else {
            await sendChat(`@${username} Something went wrong. Try again!`)
          }
        } else {
          // Default to plant
          let type
          const plantArg = entityType || entityKind
          if (LEGENDARY_PLANT_TYPES.includes(plantArg)) {
            const cosmeticId = `plant_${plantArg}`
            if (!settings.enableBitsFeatures || !bitsStore.hasCosmetic(userId, cosmeticId)) {
              await sendChat(`@${username} ${plantArg} is a legendary plant! Use 500 bits to unlock it.`)
              return
            }
            type = plantArg
          } else {
            type = ALL_PLANT_TYPES.includes(plantArg) ? plantArg : settings.defaultPlantType || 'flower'
          }

          const result = await store.addPlant(userId, username, type)
          if (result.success) {
            await sendChat(`🌱 @${username} planted a ${type}! Water it with !water`)
          } else if (result.error === 'max_plants') {
            await sendChat(`@${username} The terrarium is full of plants!`)
          } else {
            await sendChat(`@${username} Something went wrong. Try again!`)
          }
        }
      }
    })

    // !leave - Remove own entity
    this.commands.set('leave', {
      handler: async (userId, username, args, store) => {
        const entity = store.getEntity(userId)
        if (!entity) {
          await sendChat(`@${username} You don't have anything in the terrarium!`)
          return
        }

        if (entity.type === 'plant') {
          await store.removePlant(userId)
          await sendChat(`@${username} Your plant has been removed.`)
        } else {
          await store.removeInsect(userId)
          await sendChat(`@${username} Your insect has left the terrarium.`)
        }
      }
    })

    // ============================================
    // PLANT COMMANDS
    // ============================================

    // !water [@username] - Water own plant or someone else's
    this.commands.set('water', {
      handler: async (userId, username, args, store) => {
        const settings = getSettings()

        let targetUserId = userId
        let targetUsername = username

        if (args.length > 0) {
          // Water someone else's plant
          const targetName = args[0].replace('@', '').toLowerCase()
          const targetPlant = store.getPlantByUsername(targetName)
          if (!targetPlant) {
            await sendChat(`@${username} No plant found for ${targetName}!`)
            return
          }
          targetUserId = targetPlant.userId
          targetUsername = targetPlant.username
        } else {
          // Water own plant
          const entity = store.getEntity(userId)
          if (!entity || entity.type !== 'plant') {
            await sendChat(`@${username} You don't have a plant! Use !join plant [type] or specify a user: !water @username`)
            return
          }
        }

        if (!settings.allowWatering) {
          const isMod = await twitchApi.checkModerator(userId)
          if (!isMod) return
        }

        const result = await store.waterPlant(targetUserId, userId, username)
        if (result.success) {
          const plants = store.plants
          const plant = plants[targetUserId]
          if (targetUserId === userId) {
            await sendChat(`💧 @${username} watered their ${plant?.plantType}! Health: ${plant?.health}%`)
          } else {
            await sendChat(`💧 @${username} watered ${targetUsername}'s ${plant?.plantType}! Health: ${plant?.health}%`)
          }
        }
      }
    })

    // !bloom - Activate bloom animation (at 100%)
    this.commands.set('bloom', {
      handler: async (userId, username, args, store) => {
        const entity = store.getEntity(userId)
        if (!entity || entity.type !== 'plant') {
          await sendChat(`@${username} You don't have a plant!`)
          return
        }
        if (entity.entity.growthStage < 100) {
          await sendChat(`@${username} Your plant is only ${entity.entity.growthStage}% grown. Needs 100% to bloom!`)
          return
        }
        store.bloomPlant(userId)
        await sendChat(`🌸 @${username}'s ${entity.entity.plantType} is blooming beautifully!`)
      }
    })

    // !harvest - Harvest own mature plant
    this.commands.set('harvest', {
      handler: async (userId, username, args, store) => {
        const entity = store.getEntity(userId)
        if (!entity || entity.type !== 'plant') {
          await sendChat(`@${username} You don't have a plant to harvest!`)
          return
        }
        if (entity.entity.growthStage < 100) {
          await sendChat(`@${username} Your ${entity.entity.plantType} is only ${Math.floor(entity.entity.growthStage)}% grown! It needs to be 100% to harvest.`)
          return
        }
        const plantType = entity.entity.plantType
        await store.removePlant(userId)
        await sendChat(`🎉 @${username} harvested their ${plantType}! Use !join plant [type] to plant a new one.`)
      }
    })

    // ============================================
    // INSECT COMMANDS
    // ============================================

    // !fly - Fly to a new random position
    this.commands.set('fly', {
      handler: async (userId, username, args, store) => {
        const entity = store.getEntity(userId)
        if (!entity || entity.type !== 'insect') {
          await sendChat(`@${username} You need to be an insect to fly! Use !join insect [type]`)
          return
        }
        store.flyInsect(userId)
      }
    })

    // !land [@username] - Land on a random or specific plant
    this.commands.set('land', {
      handler: async (userId, username, args, store) => {
        const entity = store.getEntity(userId)
        if (!entity || entity.type !== 'insect') {
          await sendChat(`@${username} You need to be an insect to land!`)
          return
        }

        const plants = store.plants
        const plantKeys = Object.keys(plants)
        if (plantKeys.length === 0) {
          await sendChat(`@${username} No plants to land on!`)
          return
        }

        let targetUserId
        if (args.length > 0) {
          const targetName = args[0].replace('@', '').toLowerCase()
          const targetPlant = store.getPlantByUsername(targetName)
          if (!targetPlant) {
            await sendChat(`@${username} No plant found for ${targetName}!`)
            return
          }
          targetUserId = targetPlant.userId
        } else {
          targetUserId = plantKeys[Math.floor(Math.random() * plantKeys.length)]
        }

        store.landInsect(userId, targetUserId)
        await sendChat(`🌿 @${username} landed on ${plants[targetUserId]?.username}'s plant!`)
      }
    })

    // !pollinate @username - Help a plant grow
    this.commands.set('pollinate', {
      handler: async (userId, username, args, store) => {
        const settings = getSettings()
        if (!settings.allowPollinating) return

        const entity = store.getEntity(userId)
        if (!entity || entity.type !== 'insect') {
          await sendChat(`@${username} You need to be an insect to pollinate!`)
          return
        }

        if (args.length === 0) {
          await sendChat(`@${username} Usage: !pollinate @username`)
          return
        }

        const targetName = args[0].replace('@', '').toLowerCase()
        const targetPlant = store.getPlantByUsername(targetName)
        if (!targetPlant) {
          await sendChat(`@${username} No plant found for ${targetName}!`)
          return
        }

        const result = await store.pollinateInsect(userId, targetPlant.userId)
        if (result.success) {
          await sendChat(`🌸 @${username} pollinated ${targetPlant.username}'s plant! It got a growth boost!`)
        }
      }
    })

    // !visit @username - Visit another entity
    this.commands.set('visit', {
      handler: async (userId, username, args, store) => {
        const settings = getSettings()
        if (!settings.allowVisiting) return

        if (args.length === 0) {
          await sendChat(`@${username} Usage: !visit @username`)
          return
        }

        const targetName = args[0].replace('@', '').toLowerCase()
        const target = store.getEntityByUsername(targetName)
        if (!target) {
          await sendChat(`@${username} ${targetName} doesn't have anything in the terrarium!`)
          return
        }

        store.visitEntity(userId, targetName)
        await sendChat(`✨ @${username} visited ${targetName}!`)
      }
    })

    // ============================================
    // FUN INTERACTION COMMANDS
    // ============================================

    // !dance - Make your insect dance
    this.commands.set('dance', {
      handler: async (userId, username, args, store) => {
        const entity = store.getEntity(userId)
        if (!entity || entity.type !== 'insect') {
          await sendChat(`@${username} You need to be an insect to dance! Use !join insect [type]`)
          return
        }
        store.danceInsect(userId)
        await sendChat(`💃 @${username}'s ${entity.entity.insectType} is dancing!`)
      }
    })

    // !pet [@username] - Pet an insect
    this.commands.set('pet', {
      handler: async (userId, username, args, store) => {
        let targetUserId = userId
        if (args.length > 0) {
          const targetName = args[0].replace('@', '').toLowerCase()
          const targetInsect = store.getInsectByUsername(targetName)
          if (!targetInsect) {
            await sendChat(`@${username} No insect found for ${targetName}!`)
            return
          }
          targetUserId = targetInsect.userId
          store.petInsect(targetUserId)
          await sendChat(`🥰 @${username} pet ${targetName}'s ${targetInsect.insectType}!`)
        } else {
          const entity = store.getEntity(userId)
          if (!entity || entity.type !== 'insect') {
            await sendChat(`@${username} Specify an insect to pet: !pet @username`)
            return
          }
          store.petInsect(userId)
          await sendChat(`🥰 @${username}'s ${entity.entity.insectType} is being pet!`)
        }
      }
    })

    // !sing - Make your insect sing/chirp
    this.commands.set('sing', {
      handler: async (userId, username, args, store) => {
        const entity = store.getEntity(userId)
        if (!entity || entity.type !== 'insect') {
          await sendChat(`@${username} You need to be an insect to sing!`)
          return
        }
        store.singInsect(userId)
        await sendChat(`🎵 @${username}'s ${entity.entity.insectType} is singing!`)
      }
    })

    // !nuzzle @username - Nuzzle another entity
    this.commands.set('nuzzle', {
      handler: async (userId, username, args, store) => {
        const entity = store.getEntity(userId)
        if (!entity || entity.type !== 'insect') {
          await sendChat(`@${username} You need to be an insect to nuzzle!`)
          return
        }
        if (args.length === 0) {
          await sendChat(`@${username} Usage: !nuzzle @username`)
          return
        }
        const targetName = args[0].replace('@', '').toLowerCase()
        const result = store.nuzzleEntity(userId, targetName)
        if (result) {
          await sendChat(`💕 @${username}'s ${entity.entity.insectType} is nuzzling ${targetName}!`)
        } else {
          await sendChat(`@${username} ${targetName} not found in the terrarium!`)
        }
      }
    })

    // !sting @username - Bee stings another entity
    this.commands.set('sting', {
      handler: async (userId, username, args, store) => {
        const entity = store.getEntity(userId)
        if (!entity || entity.type !== 'insect') {
          await sendChat(`@${username} You need to be an insect to sting!`)
          return
        }
        if (entity.entity.insectType !== 'bee') {
          await sendChat(`@${username} Only bees can sting!`)
          return
        }
        if (args.length === 0) {
          await sendChat(`@${username} Usage: !sting @username`)
          return
        }
        const targetName = args[0].replace('@', '').toLowerCase()
        const result = store.stingEntity(userId, targetName)
        if (result) {
          await sendChat(`🐝💉 @${username}'s bee stung @${targetName}! Ouch!`)
        } else {
          await sendChat(`@${username} ${targetName} not found in the terrarium!`)
        }
      }
    })

    // Hat commands — give a hat to an insect (lasts 24 hours)
    const hatTypes = {
      birthdayhat: { id: 'birthday', name: 'Birthday Hat', emoji: '🎂' },
      tophat: { id: 'tophat', name: 'Top Hat', emoji: '🎩' },
      crown: { id: 'crown', name: 'Crown', emoji: '👑' },
      santahat: { id: 'santa', name: 'Santa Hat', emoji: '🎅' },
      cowboyhat: { id: 'cowboy', name: 'Cowboy Hat', emoji: '🤠' },
      wizardhat: { id: 'wizard', name: 'Wizard Hat', emoji: '🧙' }
    }

    for (const [cmd, hatInfo] of Object.entries(hatTypes)) {
      this.commands.set(cmd, {
        handler: async (userId, username, args, store) => {
          if (args.length === 0) {
            await sendChat(`@${username} Usage: !${cmd} @username`)
            return
          }
          const targetName = args[0].replace('@', '').toLowerCase()
          const targetInsect = store.getInsectByUsername(targetName)
          if (!targetInsect) {
            await sendChat(`@${username} No insect found for ${targetName}!`)
            return
          }
          const targetUserId = targetInsect.userId
          store.setInsectHat(targetUserId, hatInfo.id)
          await sendChat(`${hatInfo.emoji} @${username} gave @${targetName}'s ${targetInsect.insectType} a ${hatInfo.name}!`)
        }
      })
    }

    // !removehat - Remove your own hat
    this.commands.set('removehat', {
      handler: async (userId, username, args, store) => {
        const entity = store.getEntity(userId)
        if (!entity || entity.type !== 'insect') {
          await sendChat(`@${username} You need to be an insect to have a hat!`)
          return
        }
        store.setInsectHat(userId, null)
        await sendChat(`@${username} removed their hat.`)
      }
    })

    // !shake - Shake your plant
    this.commands.set('shake', {
      handler: async (userId, username, args, store) => {
        const entity = store.getEntity(userId)
        if (!entity || entity.type !== 'plant') {
          await sendChat(`@${username} You need to be a plant to shake!`)
          return
        }
        store.shakePlant(userId)
        await sendChat(`🌿 @${username}'s ${entity.entity.plantType} is shaking!`)
      }
    })


    // !wave - Wave (plant shakes, insect dances)
    this.commands.set('wave', {
      handler: async (userId, username, args, store) => {
        const entity = store.getEntity(userId)
        if (!entity) {
          await sendChat(`@${username} You don't have an entity in the terrarium!`)
          return
        }
        if (entity.type === 'plant') {
          store.shakePlant(userId)
          await sendChat(`👋 @${username}'s ${entity.entity.plantType} is waving!`)
        } else {
          store.danceInsect(userId)
          await sendChat(`👋 @${username}'s ${entity.entity.insectType} is waving!`)
        }
      }
    })

    // ============================================
    // INFO COMMANDS
    // ============================================

    // !plants - List plant types
    this.commands.set('plants', {
      handler: async () => {
        await sendChat(`🌿 Plants: ${PLANT_TYPES.join(', ')} | Legendary (500 bits): ${LEGENDARY_PLANT_TYPES.join(', ')}`)
      }
    })

    // !insects - List insect types
    this.commands.set('insects', {
      handler: async () => {
        await sendChat(`🐛 Insects: ${INSECT_TYPES.join(', ')} | Legendary (500 bits): ${LEGENDARY_INSECT_TYPES.join(', ')}`)
      }
    })

    // !help / !terrarium / !commands - Show help in chat
    this.commands.set('help', {
      handler: async () => {
        await sendChat(`🌿 Stream Terrarium — !join plant [type] or !join insect [type] to start! Commands: !water, !fly, !dance, !sing, !pet, !sting, !nuzzle, !wave, !shake, !bloom, !harvest, !pollinate, !visit, !land. Hats: !birthdayhat, !crown, !tophat, !wizardhat, !cowboyhat, !santahat. Info: !plants, !insects, !shop`)
      }
    })

    this.commands.set('terrarium', {
      handler: async () => {
        await sendChat(`🌿 Stream Terrarium — A living garden in your stream! Type !join plant [type] or !join insect [type] to enter. Use !help for all commands.`)
      }
    })

    this.commands.set('commands', {
      handler: async () => {
        await sendChat(`🌿 Commands: !join, !leave, !water, !bloom, !harvest, !shake, !wave, !fly, !land, !pollinate, !visit, !dance, !pet, !sing, !nuzzle, !sting, !birthdayhat, !crown, !tophat, !wizardhat, !cowboyhat, !santahat, !removehat, !plants, !insects, !shop, !mybits, !equip, !unequip`)
      }
    })

    // ============================================
    // ADMIN / MOD COMMANDS
    // ============================================

    // !addplant @username [type]
    this.commands.set('addplant', {
      requiresMod: true,
      handler: async (userId, username, args, store) => {
        if (args.length === 0) {
          await sendChat(`@${username} Usage: !addplant @username [type]`)
          return
        }

        const targetName = args[0].replace('@', '')
        const plantType = args[1]?.toLowerCase() || getSettings().defaultPlantType || 'flower'

        if (!PLANT_TYPES.includes(plantType)) {
          await sendChat(`@${username} Invalid plant type. Available: ${PLANT_TYPES.join(', ')}`)
          return
        }

        const pseudoId = `manual_${targetName}_${Date.now()}`
        const result = await store.addPlant(pseudoId, targetName, plantType)
        if (result.success) {
          await sendChat(`🌱 @${username} planted a ${plantType} for ${targetName}!`)
        } else {
          await sendChat(`@${username} Failed to add plant: ${result.error}`)
        }
      }
    })

    // !addinsect @username [type]
    this.commands.set('addinsect', {
      requiresMod: true,
      handler: async (userId, username, args, store) => {
        if (args.length === 0) {
          await sendChat(`@${username} Usage: !addinsect @username [type]`)
          return
        }

        const targetName = args[0].replace('@', '')
        const insectType = args[1]?.toLowerCase() || getSettings().defaultInsectType || 'ladybug'

        if (!INSECT_TYPES.includes(insectType)) {
          await sendChat(`@${username} Invalid insect type. Available: ${INSECT_TYPES.join(', ')}`)
          return
        }

        const pseudoId = `manual_${targetName}_${Date.now()}`
        const result = await store.addInsect(pseudoId, targetName, insectType)
        if (result.success) {
          await sendChat(`🐛 @${username} added a ${insectType} for ${targetName}!`)
        } else {
          await sendChat(`@${username} Failed to add insect: ${result.error}`)
        }
      }
    })

    // !removeentity @username
    this.commands.set('removeentity', {
      requiresMod: true,
      handler: async (userId, username, args, store) => {
        if (args.length === 0) {
          await sendChat(`@${username} Usage: !removeentity @username`)
          return
        }

        const targetName = args[0].replace('@', '')
        const removed = await store.removeEntityByUsername(targetName)
        if (removed) {
          await sendChat(`🗑️ @${username} removed ${targetName} from the terrarium.`)
        } else {
          await sendChat(`@${username} ${targetName} not found in the terrarium.`)
        }
      }
    })

    // !removeplant @username
    this.commands.set('removeplant', {
      requiresMod: true,
      handler: async (userId, username, args, store) => {
        if (args.length === 0) {
          await sendChat(`@${username} Usage: !removeplant @username`)
          return
        }
        const targetName = args[0].replace('@', '')
        const removed = await store.removePlantByUsername(targetName)
        if (removed) {
          await sendChat(`🗑️ @${username} removed ${targetName}'s plant.`)
        } else {
          await sendChat(`@${username} No plant found for ${targetName}.`)
        }
      }
    })

    // !removeinsect @username
    this.commands.set('removeinsect', {
      requiresMod: true,
      handler: async (userId, username, args, store) => {
        if (args.length === 0) {
          await sendChat(`@${username} Usage: !removeinsect @username`)
          return
        }
        const targetName = args[0].replace('@', '')
        const removed = await store.removeInsectByUsername(targetName)
        if (removed) {
          await sendChat(`🗑️ @${username} removed ${targetName}'s insect.`)
        } else {
          await sendChat(`@${username} No insect found for ${targetName}.`)
        }
      }
    })

    // !waterall - Water all plants
    this.commands.set('waterall', {
      requiresMod: true,
      handler: async (userId, username, args, store) => {
        const plantCount = Object.keys(store.plants).length
        if (plantCount === 0) {
          await sendChat(`@${username} No plants to water.`)
          return
        }
        await store.waterAllPlants(userId, username)
        await sendChat(`💧 @${username} watered all ${plantCount} plants!`)
      }
    })

    // !clearterrarium - Remove everything
    this.commands.set('clearterrarium', {
      requiresMod: true,
      handler: async (userId, username, args, store) => {
        await store.clearAll()
        await sendChat(`🗑️ @${username} cleared the entire terrarium.`)
      }
    })

    // ============================================
    // BITS COMMANDS
    // ============================================

    // !shop - Show available items for bits
    this.commands.set('shop', {
      handler: async (userId, username, args) => {
        const settings = getSettings()
        if (!settings.enableBitsFeatures) return

        const category = args[0]?.toLowerCase()

        if (category === 'effects') {
          const list = EFFECT_PRODUCTS.map(e => `${e.icon} ${e.name} (${e.bits} bits)`).join(' | ')
          await sendChat(`🛒 Effects: ${list}`)
        } else if (category === 'cosmetics') {
          const list = COSMETIC_PRODUCTS.slice(0, 5).map(c => `${c.name} (${c.bits} bits)`).join(' | ')
          await sendChat(`🛒 Cosmetics: ${list} ... Use !shop cosmetics2 for more`)
        } else if (category === 'cosmetics2') {
          const list = COSMETIC_PRODUCTS.slice(5).map(c => `${c.name} (${c.bits} bits)`).join(' | ')
          await sendChat(`🛒 Cosmetics: ${list}`)
        } else if (category === 'perks') {
          const list = PERK_THRESHOLDS.map(p => `${p.icon} ${p.name} (${p.threshold} total bits)`).join(' | ')
          await sendChat(`🛒 Perks (cumulative): ${list}`)
        } else {
          await sendChat(`🛒 Shop categories: !shop effects | !shop cosmetics | !shop perks — Use bits to unlock!`)
        }
      }
    })

    // !mybits - Check your bits balance
    this.commands.set('mybits', {
      handler: async (userId, username) => {
        const settings = getSettings()
        if (!settings.enableBitsFeatures) return

        const bitsStore = useBitsStore.getState()
        const totalBits = await bitsStore.getUserBits(userId)
        await sendChat(`💎 @${username} you have ${totalBits} total bits!`)
      }
    })

    // !mycosmetics - List unlocked cosmetics
    this.commands.set('mycosmetics', {
      handler: async (userId, username) => {
        const settings = getSettings()
        if (!settings.enableBitsFeatures) return

        const bitsStore = useBitsStore.getState()
        const cosmetics = await bitsStore.getUserCosmetics(userId)

        if (cosmetics.length === 0) {
          await sendChat(`@${username} You haven't unlocked any cosmetics yet! Use bits to unlock items.`)
          return
        }

        const names = cosmetics.map(c => {
          const product = COSMETIC_PRODUCTS.find(p => p.id === c.cosmeticId)
          return product ? product.name : c.cosmeticId
        }).join(', ')

        await sendChat(`✨ @${username}'s cosmetics: ${names}`)
      }
    })

    // !equip [cosmetic_id] - Equip a cosmetic variant
    this.commands.set('equip', {
      handler: async (userId, username, args, store) => {
        const settings = getSettings()
        if (!settings.enableBitsFeatures) return

        if (args.length === 0) {
          await sendChat(`@${username} Usage: !equip [cosmetic_name] — Use !mycosmetics to see what you own`)
          return
        }

        const cosmeticName = args.join('_').toLowerCase()
        const bitsStore = useBitsStore.getState()

        // Find the cosmetic by id or partial name match
        const cosmetic = COSMETIC_PRODUCTS.find(c =>
          c.id === cosmeticName || c.id.includes(cosmeticName) || c.name.toLowerCase().includes(args.join(' ').toLowerCase())
        )

        if (!cosmetic) {
          await sendChat(`@${username} Cosmetic not found. Use !shop cosmetics to see available items.`)
          return
        }

        // Check if user has it (fetch from server to ensure fresh data)
        await bitsStore.getUserCosmetics(userId, true) // force refresh
        if (!bitsStore.hasCosmetic(userId, cosmetic.id)) {
          await sendChat(`@${username} You haven't unlocked ${cosmetic.name}! Use ${cosmetic.bits} bits to unlock it.`)
          return
        }

        // Apply cosmetic to their entity
        const entity = store.getEntity(userId)
        if (!entity) {
          await sendChat(`@${username} You need to be in the terrarium first! Use !join`)
          return
        }

        // Legendary plants — swap plant type
        if (cosmetic.type === 'legendary_plant') {
          if (entity.type !== 'plant') {
            await sendChat(`@${username} You need to be a plant to equip ${cosmetic.name}! Use !leave then !join ${cosmetic.id.replace('plant_', '')}`)
            return
          }
          const newType = cosmetic.id.replace('plant_', '')
          store.setPlantType(userId, newType)
          await sendChat(`✨ @${username}'s plant transformed into a ${cosmetic.name}!`)
          return
        }

        // Legendary insects — swap insect type
        if (cosmetic.type === 'legendary_insect') {
          if (entity.type !== 'insect') {
            await sendChat(`@${username} You need to be an insect to equip ${cosmetic.name}! Use !leave then !join ${cosmetic.id.replace('insect_', '')}`)
            return
          }
          const newType = cosmetic.id.replace('insect_', '')
          store.setInsectType(userId, newType)
          await sendChat(`✨ @${username}'s insect transformed into a ${cosmetic.name}!`)
          return
        }

        // Name color — auto-applied, nothing to equip
        if (cosmetic.type === 'name_color') {
          await sendChat(`✨ @${username} Custom name color is active! It's automatically applied above your entity.`)
          return
        }

        // Variant cosmetics — apply skin with entity type validation
        const plantVariants = { 'golden_pot': 'golden', 'plant_neon': 'neon', 'plant_crystal': 'crystal', 'plant_galaxy': 'galaxy' }
        const insectVariants = { 'insect_golden': 'golden', 'insect_rainbow': 'rainbow', 'insect_shadow': 'shadow' }

        if (plantVariants[cosmetic.id]) {
          if (entity.type !== 'plant') {
            await sendChat(`@${username} ${cosmetic.name} is a plant cosmetic! You need to be a plant to use it.`)
            return
          }
          store.setEntityVariant(userId, plantVariants[cosmetic.id])
          await sendChat(`✨ @${username} equipped ${cosmetic.name}!`)
        } else if (insectVariants[cosmetic.id]) {
          if (entity.type !== 'insect') {
            await sendChat(`@${username} ${cosmetic.name} is an insect cosmetic! You need to be an insect to use it.`)
            return
          }
          store.setEntityVariant(userId, insectVariants[cosmetic.id])
          await sendChat(`✨ @${username} equipped ${cosmetic.name}!`)
        } else {
          await sendChat(`@${username} Unknown cosmetic: ${cosmetic.id}`)
        }
      }
    })

    // !rename [name] - Rename your plant (5000 bits perk)
    this.commands.set('rename', {
      handler: async (userId, username, args, store) => {
        const settings = getSettings()
        if (!settings.enableBitsFeatures) return

        const bitsStore = useBitsStore.getState()
        if (!bitsStore.hasReachedPerk(userId, 'custom_name')) {
          await sendChat(`@${username} You need 5,000 total bits to unlock custom plant names!`)
          return
        }

        const entity = store.getEntity(userId)
        if (!entity || entity.type !== 'plant') {
          await sendChat(`@${username} You need a plant to rename!`)
          return
        }

        const newName = args.join(' ').slice(0, 20)
        if (!newName) {
          await sendChat(`@${username} Usage: !rename [name] (max 20 chars)`)
          return
        }

        store.setCustomName(userId, newName)
        await sendChat(`✏️ @${username}'s plant is now named "${newName}"!`)
      }
    })

    // !unequip - Remove cosmetic variant
    this.commands.set('unequip', {
      handler: async (userId, username, args, store) => {
        const settings = getSettings()
        if (!settings.enableBitsFeatures) return

        store.setEntityVariant(userId, 'default')
        await sendChat(`@${username} Cosmetic removed, back to default!`)
      }
    })

    // Legacy aliases
    this.commands.set('cleargarden', {
      requiresMod: true,
      handler: async (userId, username, args, store) => {
        await store.clearAll()
        await sendChat(`🗑️ @${username} cleared the entire terrarium.`)
      }
    })
  }

  async processMessage(chatMessage) {
    const { username, userId, message } = chatMessage
    const store = useTerrariumStore.getState()
    const settings = getSettings()

    // Show chat bubble for non-command messages from users who have entities
    if (!message.startsWith('!')) {
      if (store.hasEntity(userId)) {
        store.showChatBubble(userId, message)
      }
      return
    }

    // Parse command
    const parts = message.slice(1).split(' ')
    const commandName = parts[0].toLowerCase()
    const args = parts.slice(1)

    const command = this.commands.get(commandName)
    if (!command) return

    // Check tier requirement
    if (command.requiresTier && settings.requireSubscription) {
      try {
        const subData = await twitchApi.checkSubscription(userId)
        if (!subData || subData.tier < (settings.requiredTier || 1000)) {
          console.log(`${username} doesn't meet tier requirement`)
          return
        }
      } catch {
        // If check fails, let it through (graceful degradation)
      }
    }

    // Check mod requirement
    if (command.requiresMod) {
      if (settings.broadcasterOnlyAdmin) {
        const isBroadcaster = await twitchApi.checkIsBroadcaster(userId)
        if (!isBroadcaster) return
      } else if (settings.modCanUseAdminCommands) {
        const isMod = await twitchApi.checkModerator(userId)
        if (!isMod) return
      } else {
        const isBroadcaster = await twitchApi.checkIsBroadcaster(userId)
        if (!isBroadcaster) return
      }
    }

    try {
      await command.handler(userId, username, args, store)
    } catch (error) {
      console.error(`Error executing command ${commandName}:`, error)
    }
  }
}

export const commandHandler = new CommandHandler()